import { frameDisplayUrl } from "../utils/assetMatching";
import { useMemo } from "react";
import type { EvoLinkCategory, ExportScope, FilmProject, PackageSourceMapFrame, PackageSourceMapSnapshot } from "../types";
import { buildPackageSourceMapSnapshot } from "../utils/packageSourceMap";
import { formatRuntime } from "../utils/storyboard";

interface PackageSourceMapProps {
  project?: FilmProject;
  selectedSceneId?: string;
  scope?: ExportScope;
  categories?: EvoLinkCategory[];
  snapshot?: PackageSourceMapSnapshot;
  title?: string;
  subtitle?: string;
  compact?: boolean;
  defaultCollapsed?: boolean;
}

function compactValues(values: string[], empty: string): string {
  const filtered = values.filter(Boolean);
  if (!filtered.length) return empty;
  if (filtered.length <= 3) return filtered.join(" · ");
  return `${filtered.slice(0, 3).join(" · ")} · +${filtered.length - 3}`;
}

function laneLabels(frame: PackageSourceMapFrame, lane: "characters" | "voices" | "references"): string[] {
  return frame[lane].map((item) => {
    if (item.status === "excluded") return item.label;
    return `${item.label}: ${item.detail}`;
  });
}

function assignmentModeLabel(mode: PackageSourceMapFrame["assignmentMode"]): string {
  return mode === "board_override" ? "Board override" : "Scene defaults";
}

function blockingMarksLabel(frame: PackageSourceMapFrame): string {
  const marks = frame.blockingPlan?.characterMarks?.map((mark) => (
    [mark.character, mark.framePosition].filter(Boolean).join(" @ ")
  )) || [];
  return compactValues(marks, "No marks");
}

function sourceMapWarningCount(sourceMap: PackageSourceMapSnapshot): number {
  return sourceMap.scenes.reduce((sceneTotal, scene) => (
    sceneTotal + scene.frames.reduce((frameTotal, frame) => {
      const laneWarnings = [...frame.characters, ...frame.voices, ...frame.references]
        .filter((item) => item.status === "missing" || item.status === "disabled").length;
      return frameTotal + laneWarnings + (frame.enabled ? 0 : 1);
    }, 0)
  ), 0);
}

export function PackageSourceMap({
  project,
  selectedSceneId,
  scope,
  categories,
  snapshot,
  title = "Source Map",
  subtitle,
  compact = false,
  defaultCollapsed = false
}: PackageSourceMapProps) {
  const sourceMap = useMemo(() => {
    if (snapshot) return snapshot;
    if (!project || !selectedSceneId || !scope || !categories) return undefined;
    return buildPackageSourceMapSnapshot(project, selectedSceneId, scope, categories);
  }, [categories, project, scope, selectedSceneId, snapshot]);

  if (!sourceMap) return null;

  const warningCount = sourceMapWarningCount(sourceMap);
  const header = (
      <header>
        <div>
          <span>{title}</span>
          <strong>
            {sourceMap.totals.sceneCount} scene{sourceMap.totals.sceneCount === 1 ? "" : "s"} · {sourceMap.totals.boardCount} board{sourceMap.totals.boardCount === 1 ? "" : "s"}
          </strong>
        </div>
        <small>
          {subtitle
            || (sourceMap.totals.customBoardOverrideCount
              ? `${sourceMap.totals.customBoardOverrideCount} custom board override${sourceMap.totals.customBoardOverrideCount === 1 ? "" : "s"}`
              : "All boards use scene defaults")}
        </small>
      </header>
  );

  const summary = (
      <div className="source-map-summary-grid" aria-label={`${title} summary`}>
        <div>
          <span>Scenes</span>
          <strong>{sourceMap.totals.sceneCount}</strong>
        </div>
        <div>
          <span>Included boards</span>
          <strong>{sourceMap.totals.includedBoardCount}</strong>
        </div>
        <div>
          <span>Overrides</span>
          <strong>{sourceMap.totals.customBoardOverrideCount}</strong>
        </div>
        <div className={warningCount ? "warn" : "ready"}>
          <span>Warnings</span>
          <strong>{warningCount}</strong>
        </div>
      </div>
  );

  const body = (
      <div className="source-map-scene-list">
        {sourceMap.scenes.map((scene, index) => (
          <details className="source-map-scene" key={scene.sceneId} open={index === 0}>
            <summary>
              <span>{`Scene ${String(scene.sceneNumber).padStart(2, "0")}`}</span>
              <strong>{scene.slugline}</strong>
              <small>{scene.includedBoardCount} included · {formatRuntime(scene.runtimeSeconds)}</small>
            </summary>

            <div className="source-map-frame-list">
              {scene.frames.map((frame) => (
                <article className={frame.enabled ? "" : "disabled"} key={frame.frameId}>
                  <img src={frameDisplayUrl(frame)} alt="" />
                  <div className="source-map-frame-main">
                    <div className="source-map-frame-heading">
                      <div>
                        <strong>{frame.shotType}</strong>
                        <small>{frame.status} · {assignmentModeLabel(frame.assignmentMode)}</small>
                      </div>
                      <span>
                        {frame.timing.label} · {frame.timing.runtime}
                        {frame.timing.locked ? " · locked" : ""}
                      </span>
                    </div>

                    <div className="source-map-lanes">
                      <div>
                        <span>Characters</span>
                        <strong>{compactValues(laneLabels(frame, "characters"), "No characters")}</strong>
                      </div>
                      <div>
                        <span>Voices</span>
                        <strong>{compactValues(laneLabels(frame, "voices"), "No voices")}</strong>
                      </div>
                      <div>
                        <span>References</span>
                        <strong>{compactValues(laneLabels(frame, "references"), "No references")}</strong>
                      </div>
                      <div>
                        <span>Look</span>
                        <strong>{`${frame.look.style} · ${frame.look.colorGrade} · ${frame.look.aspect} · ${frame.look.resolution}`}</strong>
                      </div>
                    </div>

                    {frame.blockingPlan ? (
                      <div className="source-map-blocking">
                        <div>
                          <span>Blocking</span>
                          <strong>{frame.blockingPlan.objectMark || blockingMarksLabel(frame)}</strong>
                        </div>
                        <small>
                          {[frame.blockingPlan.movementPath, frame.blockingPlan.cameraAxis, frame.blockingPlan.endFrame]
                            .filter(Boolean)
                            .join(" · ")}
                        </small>
                      </div>
                    ) : null}

                    <p>{frame.prompt}</p>
                  </div>
                </article>
              ))}
            </div>
          </details>
        ))}
      </div>
  );

  const advancedBody = (
      <details className="advanced-drawer compact-drawer source-map-advanced">
        <summary>Advanced source-map detail</summary>
        {body}
      </details>
  );

  return (
    <section className={`package-source-map ${compact ? "compact" : ""}`} aria-label={title}>
      {defaultCollapsed ? (
        <details className="source-map-details">
          <summary>{header}</summary>
          {summary}
          {advancedBody}
        </details>
      ) : (
        <>
          {header}
          {summary}
          {body}
        </>
      )}
    </section>
  );
}
